import {BOOLEAN_FEATURE_FLAGS} from "../constants/featureFlags";
import type {TenantFeatureConfig, TenantSummaryResponse} from "../types/tenant";
import {calendarDaysUntil} from "../util/format";
import {hasActiveSubscriptionAccess} from "./subscriptionDates";

export type TenantStatus = "paying" | "free" | "expired" | "noSubscription";

export type TenantLifecycle = {
    status: TenantStatus;
    /** Access (not the paid period) ends within the next 30 days. */
    expiringSoon: boolean;
    /** Calendar days until access ends; negative once it has. */
    accessDaysLeft: number | null;
};

export const EXPIRING_SOON_DAYS = 30;

export type TenantSegment = "all" | "paying" | "free" | "expiring" | "expired";

export type TenantSegmentCounts = Record<TenantSegment, number>;

/** Status follows access end (`activeUntil`), the same rule the expiry warning and the delete gate use. */
export function getTenantLifecycle(tenant: TenantSummaryResponse, now: Date): TenantLifecycle {
    const subscription = tenant.activeSubscription;
    if (!subscription) return {status: "noSubscription", expiringSoon: false, accessDaysLeft: null};

    const accessDaysLeft = calendarDaysUntil(subscription.activeUntil, now);
    if (!hasActiveSubscriptionAccess(subscription, now)) return {status: "expired", expiringSoon: false, accessDaysLeft};

    return {
        status: subscription.amountDueInCents > 0 ? "paying" : "free",
        expiringSoon: accessDaysLeft <= EXPIRING_SOON_DAYS,
        accessDaysLeft,
    };
}

export function matchesSegment(tenant: TenantSummaryResponse, segment: TenantSegment, now: Date): boolean {
    const lifecycle = getTenantLifecycle(tenant, now);
    switch (segment) {
        case "all":
            return true;
        case "paying":
        case "free":
        case "expired":
            return lifecycle.status === segment;
        case "expiring":
            return lifecycle.expiringSoon;
    }
}

export function countTenantSegments(tenants: TenantSummaryResponse[], now: Date): TenantSegmentCounts {
    const counts: TenantSegmentCounts = {all: 0, paying: 0, free: 0, expiring: 0, expired: 0};
    for (const tenant of tenants) {
        const lifecycle = getTenantLifecycle(tenant, now);
        counts.all += 1;
        if (lifecycle.status === "paying" || lifecycle.status === "free" || lifecycle.status === "expired") {
            counts[lifecycle.status] += 1;
        }
        if (lifecycle.expiringSoon) counts.expiring += 1;
    }
    return counts;
}

/** Stored overrides set to on; inherited (`null`) flags are not counted because their effective value is global. */
export function enabledFeatureCount(featureConfig: TenantFeatureConfig | undefined): number {
    if (!featureConfig) return 0;
    return BOOLEAN_FEATURE_FLAGS.filter((flag) => featureConfig[flag] === true).length;
}
